import React, { useEffect } from 'react';
import { Link } from '../lib/routerCompat';
import { SITE_TAGLINE } from '../config/site';
import WaitlistForm from '../components/WaitlistForm';
import LazyImage from '../components/LazyImage';

const CREATOR_POINTS = [
  'Apply to campaigns with real budgets ($250 minimum)',
  'Get discovered by brands actively looking for talent',
  'Submit pitches directly inside the platform',
  'Deliver content and get paid — all in one place',
];

const BRAND_POINTS = [
  'Post campaigns with clear deliverables',
  'Browse vetted creators across niches',
  'Receive pitches instead of cold outreach',
  'Manage everything — from brief to delivery — in one system',
];

const WHY_POINTS = [
  {
    title: 'Minimum Campaign Budget',
    text: 'Every campaign starts at $250 — quality is enforced, not hoped for.',
  },
  {
    title: 'All-In-One Platform',
    text: 'Briefs, pitches, delivery, and payment live in one secure system.',
  },
  {
    title: 'No Off-Platform Deals',
    text: 'Everything stays protected. No side channels, no burned creators.',
  },
  {
    title: 'Built For Serious Results',
    text: 'Designed for scale, speed, and creators who treat content like a business.',
  },
];

const AUDIENCES = ['UGC Creators', 'Influencers', 'Brands & Agencies', 'Digital Entrepreneurs'];

function scrollToWaitlist(e: React.MouseEvent<HTMLAnchorElement>): void {
  e.preventDefault();
  const el = document.getElementById('waitlist');
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
    // preventDefault suppresses the hash update; restore it so the CTA
    // stays deep-linkable and back/forward behave normally
    window.history.pushState(null, '', '#waitlist');
  }
}

interface PointListProps {
  /** The list of point labels to render as gem-prefixed bullet items. */
  points: string[];
}

function PointList({ points }: PointListProps): JSX.Element {
  return (
    <ul className="landing-points">
      {points.map((p) => (
        <li key={p}>
          <span className="landing-point-gem">◆</span>
          {p}
        </li>
      ))}
    </ul>
  );
}

function LandingHeader(): JSX.Element {
  return (
    <header className="landing-header">
      <Link to="/" className="nav-logo">
        <LazyImage
          src="/logo.png"
          alt="PitchVault"
          className="header-logo"
          width={56}
          height={56}
          eager
        />
        Pitch Vault
      </Link>
      <div className="landing-header-actions">
        <Link to="/auth" className="btn-outline landing-header-btn">
          Sign In
        </Link>
        <a href="#waitlist" onClick={scrollToWaitlist} className="btn-gold landing-header-btn">
          Join the Waitlist
        </a>
      </div>
    </header>
  );
}

function RiskDisclaimer(): JSX.Element {
  return (
    <section className="landing-disclaimer" role="region" aria-label="Risk disclaimer">
      <span className="landing-disclaimer-icon" aria-hidden="true">
        ⚠
      </span>
      <p className="landing-disclaimer-text">
        <strong>Use at your own risk.</strong> PitchVault is provided &ldquo;as is,&rdquo; without
        warranties of any kind. We are not liable for losses arising from campaigns, payments, or
        dealings between creators and brands. By using this service, you accept full responsibility
        for your own decisions.
      </p>
    </section>
  );
}

function Hero(): JSX.Element {
  return (
    <section className="landing-hero">
      <div className="landing-eyebrow">A Premium UGC Marketplace</div>
      <h1 className="landing-title gold-text">{SITE_TAGLINE}</h1>
      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>
      <p className="landing-subtitle">
        PitchVault connects serious creators with real brands ready to spend.
        <br />
        No lowball offers. No spam. No wasted time — just high-quality campaigns, direct access, and
        real payouts.
      </p>
      <div className="landing-cta-row">
        <a href="#waitlist" onClick={scrollToWaitlist} className="btn-gold landing-cta">
          Join the Waitlist
        </a>
        <Link to="/auth" className="btn-outline landing-cta">
          Preview the Platform
        </Link>
      </div>
      <div className="landing-trust-strip">
        <span className="creator-badge badge-verified">✓ Verified Creators</span>
        <span className="creator-badge badge-top">◆ $250 Minimum Campaigns</span>
        <span className="creator-badge badge-fast">⚡ Escrow-Protected Payouts</span>
      </div>
    </section>
  );
}

function Features(): JSX.Element {
  return (
    <section className="landing-section" id="features">
      <div className="landing-columns">
        <div className="landing-card">
          <div className="section-header">For Creators</div>
          <p className="landing-card-lead">Stop chasing deals. Start getting selected.</p>
          <PointList points={CREATOR_POINTS} />
          <p className="landing-card-note">
            PitchVault is built for creators who treat content like a business.
          </p>
        </div>
        <div className="landing-card">
          <div className="section-header">For Brands</div>
          <p className="landing-card-lead">Find creators who actually convert.</p>
          <PointList points={BRAND_POINTS} />
          <p className="landing-card-note">No middlemen. No noise. Just execution.</p>
        </div>
      </div>
    </section>
  );
}

function WhyPitchVault(): JSX.Element {
  return (
    <section className="landing-section">
      <div className="section-header landing-section-title">Why PitchVault</div>
      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>
      <div className="landing-why-grid">
        {WHY_POINTS.map((w) => (
          <div className="landing-why-item" key={w.title}>
            <div className="landing-why-title">{w.title}</div>
            <p className="landing-why-text">{w.text}</p>
          </div>
        ))}
      </div>
      <div className="landing-audience-row">
        {AUDIENCES.map((a) => (
          <span className="filter-chip on landing-audience-chip" key={a}>
            {a}
          </span>
        ))}
      </div>
    </section>
  );
}

function PricingTeaser(): JSX.Element {
  return (
    <section className="landing-section" id="pricing">
      <div className="section-header landing-section-title">Simple, Serious Pricing</div>
      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>
      <div className="landing-columns landing-pricing">
        <div className="landing-card landing-price-card">
          <div className="landing-price-role">Creators</div>
          <div className="landing-price">
            $10.99<span className="landing-price-per">/month</span>
          </div>
          <div className="landing-price-alt">or $99.99/year</div>
          <p className="landing-card-note">
            One subscription. Unlimited pitches, campaigns, and payouts.
          </p>
        </div>
        <div className="landing-card landing-price-card">
          <div className="landing-price-role">Brands</div>
          <div className="landing-price">Free</div>
          <div className="landing-price-alt">browse &amp; post campaigns</div>
          <p className="landing-card-note">
            Pay only for the work you commission — minimum $250 per campaign.
          </p>
        </div>
      </div>
    </section>
  );
}

function WaitlistSection(): JSX.Element {
  return (
    <section className="landing-section landing-waitlist" id="waitlist">
      <div className="section-header landing-section-title">Be First In The Vault</div>
      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>
      <p className="landing-subtitle">
        If you&rsquo;re tired of cheap deals and wasted time&hellip; PitchVault is where you level
        up. Join the waitlist for early access.
      </p>
      <div className="landing-waitlist-slot">
        <WaitlistForm source="landing" />
      </div>
      <p className="landing-consent">
        By joining you agree to our <Link to="/terms">Terms</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </section>
  );
}

export default function LandingView(): JSX.Element {
  // The browser's native fragment scroll fires before React renders the
  // sections, so re-run it once they exist (e.g. arriving from a bio link).
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <div className="landing">
      <LandingHeader />
      <RiskDisclaimer />
      <Hero />
      <Features />
      <WhyPitchVault />
      <PricingTeaser />
      <WaitlistSection />
    </div>
  );
}
